import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { projects, cases } from '../services/api';
import { JoinWithCode } from '../components/enrollment';
import { nomUsuari } from '../utils/usuari';

const STATUS_LABELS = {
  active: 'Actiu',
  draft: 'Esborrany',
  archived: 'Arxivat'
};

const DIFFICULTY_LABELS = {
  beginner: 'Principiant',
  intermediate: 'Intermedi',
  advanced: 'Avançat'
};

function DashboardPage() {
  const { user } = useAuth();
  const [recentProjects, setRecentProjects] = useState([]);
  const [myCases, setMyCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalProjects: 0,
    activeProjects: 0,
    draftProjects: 0,
    enrolledCases: 0
  });
  const [showJoinModal, setShowJoinModal] = useState(false);

  const loadDashboardData = useCallback(async () => {
    try {
      // Load projects for instructors/admins
      if (user?.role === 'instructor' || user?.role === 'admin') {
        const { data } = await projects.getAll({ limit: 5 });
        setRecentProjects(data.data || []);

        const allProjects = data.data || [];
        setStats({
          totalProjects: data.pagination?.total || allProjects.length,
          activeProjects: allProjects.filter(p => p.status === 'active').length,
          draftProjects: allProjects.filter(p => p.status === 'draft').length,
          enrolledCases: 0
        });
      }

      // Load enrolled cases for analysts
      if (user?.role === 'analyst') {
        const { data } = await cases.getAll({ limit: 10 });
        setMyCases(data.data || []);
        setStats({
          totalProjects: 0,
          activeProjects: 0,
          draftProjects: 0,
          enrolledCases: data.pagination?.total || data.data?.length || 0
        });
      }
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.role]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  const handleJoinSuccess = () => {
    loadDashboardData();
  };

  const StatCard = ({ title, value, color }) => (
    <div className="card">
      <p className="text-sm font-medium text-content-secondary">{title}</p>
      <p className={`text-3xl font-bold ${color}`}>{value}</p>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-content">
            Benvingut/da, {nomUsuari(user)}
          </h1>
          <p className="text-content-secondary">
            {user?.role === 'admin' && 'Administrador del sistema'}
            {user?.role === 'instructor' && 'Panell d\'instructor'}
            {user?.role === 'analyst' && 'Panell d\'analista'}
          </p>
        </div>
        {/* Join with Code button for analysts */}
        {user?.role === 'analyst' && (
          <button
            onClick={() => setShowJoinModal(true)}
            className="btn btn-primary flex items-center"
          >
            <svg className="w-5 h-5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
            Unir-se amb codi
          </button>
        )}
      </div>

      {/* Stats for Instructors/Admins */}
      {(user?.role === 'instructor' || user?.role === 'admin') && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <StatCard title="Total projectes" value={stats.totalProjects} color="text-content" />
          <StatCard title="Projectes actius" value={stats.activeProjects} color="text-success" />
          <StatCard title="Projectes en esborrany" value={stats.draftProjects} color="text-warning" />
        </div>
      )}

      {/* Stats for Analysts */}
      {user?.role === 'analyst' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <StatCard title="Els meus casos" value={stats.enrolledCases} color="text-accent" />
          <div className="card bg-accent-subtle border border-accent/30">
            <p className="text-sm font-medium text-accent-hover">Necessites unir-te a un cas?</p>
            <p className="text-sm text-accent mt-1">
              Demana un codi d'invitació al teu instructor i fes clic a "Unir-se amb codi".
            </p>
          </div>
        </div>
      )}

      {/* Recent Projects for Instructors/Admins */}
      {(user?.role === 'instructor' || user?.role === 'admin') && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-content">Projectes recents</h2>
            <Link to="/projects" className="text-sm text-accent hover:text-primary-500">
              Veure tots
            </Link>
          </div>

          {recentProjects.length === 0 ? (
            <p className="text-content-muted text-center py-8">
              Encara no hi ha projectes.
              {(user?.role === 'instructor' || user?.role === 'admin') && (
                <Link to="/projects" className="block mt-2 text-accent">
                  Crea el teu primer projecte
                </Link>
              )}
            </p>
          ) : (
            <div className="space-y-3">
              {recentProjects.map((project) => (
                <Link
                  key={project.id}
                  to={`/projects/${project.id}`}
                  className="block p-4 bg-surface-subtle rounded-lg hover:bg-surface-muted transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-content">{project.title}</h3>
                      <p className="text-sm text-content-muted">{project.caseCount} {project.caseCount === 1 ? 'cas' : 'casos'}</p>
                    </div>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      project.status === 'active' ? 'bg-success-subtle text-success-text' :
                      project.status === 'draft' ? 'bg-warning-subtle text-warning-text' :
                      'bg-surface-muted text-content'
                    }`}>
                      {STATUS_LABELS[project.status] || project.status}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}

      {/* My Cases for Analysts */}
      {user?.role === 'analyst' && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-content">Els meus casos</h2>
          </div>

          {myCases.length === 0 ? (
            <div className="text-center py-8">
              <svg className="w-12 h-12 text-content-disabled mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <p className="text-content-muted">
                Encara no estàs inscrit a cap cas.
              </p>
              <button
                onClick={() => setShowJoinModal(true)}
                className="mt-4 text-accent hover:text-primary-500 font-medium"
              >
                Uneix-te a un cas amb un codi d'invitació
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {myCases.map((caseItem) => (
                <Link
                  key={caseItem.id}
                  to={`/cases/${caseItem.id}`}
                  className="block p-4 bg-surface-subtle rounded-lg hover:bg-surface-muted transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium text-content">{caseItem.title}</h3>
                      <p className="text-sm text-content-muted">
                        {caseItem.project?.title}
                        {caseItem.team && ` - Equip: ${caseItem.team.name}`}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        caseItem.difficulty === 'beginner' ? 'bg-success-subtle text-success-text' :
                        caseItem.difficulty === 'intermediate' ? 'bg-warning-subtle text-warning-text' :
                        'bg-error-subtle text-error-text'
                      }`}>
                        {DIFFICULTY_LABELS[caseItem.difficulty] || caseItem.difficulty}
                      </span>
                      {caseItem.isTeamBased && (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-primary-100 text-primary-800">
                          Equip
                        </span>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Join with Code Modal */}
      {showJoinModal && (
        <JoinWithCode
          onClose={() => setShowJoinModal(false)}
          onSuccess={handleJoinSuccess}
        />
      )}
    </div>
  );
}

export default DashboardPage;
